import { useState, useEffect } from 'react';
import Layout from '@/components/Layout';
import { useAuth } from '@/lib/authContext';
import { getNotificationSettings, updateNotificationSettings, NotificationSettings } from '@/lib/supabaseService';
import { supabase } from '@/lib/supabase';
import toast from 'react-hot-toast';
import { cn } from '@/lib/utils';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';

// ─── Shared style constants ────────────────────────
const LABEL_CLS = 'text-[10px] uppercase tracking-widest text-calm-accent mb-1.5 block';
const INPUT_CLS = 'border-calm-border rounded-huge text-sm font-light';

export default function AsesorConfiguracion() {
  const { user, updatePassword } = useAuth();
  const [loading, setLoading] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [savingEmail, setSavingEmail] = useState(false);
  const [savingNotifications, setSavingNotifications] = useState(false);

  // Password form state
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  // Email form state
  const [emailForm, setEmailForm] = useState({
    newEmail: '',
  });

  // Notification settings state
  const [notificationSettings, setNotificationSettings] = useState<NotificationSettings>({
    documentosSubidos: true,
    documentosContabilizados: true,
    incidenciasNuevas: true,
    incidenciasResueltas: true,
    informesDisponibles: true,
    recordatoriosIA: true,
    vencimientosFiscales: true,
    actualizacionesPlataforma: false,
  });

  // Load notification settings on mount
  useEffect(() => {
    if (user?.id) {
      loadNotificationSettings();
    }
  }, [user]);

  const loadNotificationSettings = async () => {
    if (!user?.id) return;

    try {
      setLoading(true);
      const settings = await getNotificationSettings(user.id);
      setNotificationSettings(settings);
    } catch (error: unknown) {
      console.error('Error loading notification settings:', error);
      toast.error('Error al cargar las preferencias de notificaciones');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmPassword) {
      toast.error('Por favor, completa todos los campos');
      return;
    }

    if (passwordForm.newPassword.length < 6) {
      toast.error('La contraseña debe tener al menos 6 caracteres');
      return;
    }

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error('Las contraseñas no coinciden');
      return;
    }

    try {
      setSavingPassword(true);

      if (passwordForm.currentPassword === passwordForm.newPassword) {
        toast.error('La nueva contraseña debe ser diferente a la contraseña actual');
        setSavingPassword(false);
        return;
      }

      if (user?.email && passwordForm.currentPassword) {
        const { error: signInError } = await supabase.auth.signInWithPassword({
          email: user.email,
          password: passwordForm.currentPassword,
        });

        if (signInError) {
          toast.error('La contraseña actual es incorrecta');
          setSavingPassword(false);
          return;
        }
      }

      await updatePassword(passwordForm.newPassword);

      toast.success('Contraseña actualizada correctamente');
      setPasswordForm({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
    } catch (error: unknown) {
      console.error('Error updating password:', error);

      const errorMessage = error instanceof Error ? error.message : '';
      if (errorMessage.includes('should be different from the old password')) {
        toast.error('La nueva contraseña debe ser diferente a tu contraseña actual');
      } else if (errorMessage.includes('Password should be at least')) {
        toast.error('La contraseña debe tener al menos 6 caracteres');
      } else {
        toast.error(errorMessage || 'Error al actualizar la contraseña');
      }
    } finally {
      setSavingPassword(false);
    }
  };

  const handleEmailChange = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!emailForm.newEmail) {
      toast.error('Por favor, ingresa un nuevo correo electrónico');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(emailForm.newEmail)) {
      toast.error('Por favor, ingresa un correo electrónico válido');
      return;
    }

    if (emailForm.newEmail === user?.email) {
      toast.error('El nuevo correo debe ser diferente al actual');
      return;
    }

    try {
      setSavingEmail(true);

      const { error } = await supabase.auth.updateUser({
        email: emailForm.newEmail,
      });

      if (error) {
        throw error;
      }

      toast.success(
        'Se ha enviado un correo de confirmación a tu nueva dirección. Por favor, verifica tu correo para completar el cambio.'
      );
      setEmailForm({ newEmail: '' });
    } catch (error: unknown) {
      console.error('Error updating email:', error);
      const msg = error instanceof Error ? error.message : 'Error al actualizar el correo electrónico';
      toast.error(msg);
    } finally {
      setSavingEmail(false);
    }
  };

  const handleNotificationChange = async (key: keyof NotificationSettings, value: boolean) => {
    if (!user?.id) return;

    const updatedSettings = {
      ...notificationSettings,
      [key]: value,
    };

    setNotificationSettings(updatedSettings);

    try {
      setSavingNotifications(true);
      await updateNotificationSettings(user.id, { [key]: value });
      toast.success('Preferencias de notificaciones actualizadas');
    } catch (error: unknown) {
      console.error('Error updating notification settings:', error);
      toast.error('Error al actualizar las preferencias de notificaciones');
      setNotificationSettings(notificationSettings);
    } finally {
      setSavingNotifications(false);
    }
  };

  if (!user) {
    return null;
  }

  // ─── Render ────────────────────────────────────────
  return (
    <Layout rol="asesor">
      <div className="h-[calc(100vh-72px)] overflow-y-auto px-12 pb-12 space-y-12 bg-calm-bg">

        {/* ═══════════════ PAGE HEADER ═══════════════ */}
        <div className="pt-12">
          <p className="text-[10px] uppercase tracking-widest text-calm-accent mb-2">Ajustes</p>
          <h1 className="font-outfit text-5xl font-light text-calm-text tracking-tight">
            Configuración
          </h1>
          <p className="text-sm text-calm-muted mt-2 font-light">
            Perfil y preferencias del asesor
          </p>
        </div>

        {/* ═══════════════ AJUSTES DE CUENTA ═══════════════ */}
        <section className="space-y-6">
          <p className="text-[10px] uppercase tracking-widest text-calm-accent">Ajustes de Cuenta</p>

          {/* Password Section */}
          <div className="bg-gradient-to-br from-white to-slate-50 border border-slate-200/60 rounded-huge p-8 hover:shadow-sm transition-all cursor-pointer group">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-slate-50 flex items-center justify-center text-calm-accent">
                <span className="material-symbols-outlined text-xl">lock</span>
              </div>
              <div>
                <h3 className="text-sm font-light text-calm-text">Cambiar Contraseña</h3>
                <p className="text-[11px] text-calm-accent font-light">Actualiza tu contraseña de acceso</p>
              </div>
            </div>
            <form onSubmit={handlePasswordChange} className="space-y-4 max-w-md">
              <div>
                <Label className={LABEL_CLS}>Contraseña Actual</Label>
                <Input
                  type="password"
                  id="currentPassword"
                  value={passwordForm.currentPassword}
                  onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
                  placeholder="Ingresa tu contraseña actual"
                  required
                  className={INPUT_CLS}
                />
              </div>
              <div>
                <Label className={LABEL_CLS}>Nueva Contraseña</Label>
                <Input
                  type="password"
                  id="newPassword"
                  value={passwordForm.newPassword}
                  onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                  placeholder="Mínimo 6 caracteres"
                  minLength={6}
                  required
                  className={INPUT_CLS}
                />
              </div>
              <div>
                <Label className={LABEL_CLS}>Confirmar Nueva Contraseña</Label>
                <Input
                  type="password"
                  id="confirmPassword"
                  value={passwordForm.confirmPassword}
                  onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                  placeholder="Confirma tu nueva contraseña"
                  required
                  className={INPUT_CLS}
                />
              </div>
              <button
                type="submit"
                disabled={savingPassword}
                className="border border-calm-text rounded-huge px-5 py-2.5 text-sm font-light bg-calm-text text-white hover:bg-slate-800 transition-all disabled:opacity-40 flex items-center gap-2"
              >
                <span className="material-symbols-outlined text-base">save</span>
                {savingPassword ? 'Guardando...' : 'Actualizar Contraseña'}
              </button>
            </form>
          </div>

          {/* Email Section */}
          <div className="bg-gradient-to-br from-white to-slate-50 border border-slate-200/60 rounded-huge p-8 hover:shadow-sm transition-all cursor-pointer group">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-slate-50 flex items-center justify-center text-calm-accent">
                <span className="material-symbols-outlined text-xl">mail</span>
              </div>
              <div>
                <h3 className="text-sm font-light text-calm-text">Cambiar Correo Electrónico</h3>
                <p className="text-[11px] text-calm-accent font-light">Actualiza tu dirección de correo</p>
              </div>
            </div>
            <div className="bg-calm-bg rounded-huge p-4 mb-4 max-w-md">
              <p className="text-[10px] uppercase tracking-tighter text-calm-accent">Correo actual</p>
              <p className="text-sm font-light text-calm-text mt-1">{user.email}</p>
            </div>
            <form onSubmit={handleEmailChange} className="space-y-4 max-w-md">
              <div>
                <Label className={LABEL_CLS}>Nuevo Correo Electrónico</Label>
                <Input
                  type="email"
                  id="newEmail"
                  value={emailForm.newEmail}
                  onChange={(e) => setEmailForm({ newEmail: e.target.value })}
                  placeholder="nuevo@correo.com"
                  required
                  className={INPUT_CLS}
                />
              </div>
              <button
                type="submit"
                disabled={savingEmail}
                className="border border-calm-text rounded-huge px-5 py-2.5 text-sm font-light bg-calm-text text-white hover:bg-slate-800 transition-all disabled:opacity-40 flex items-center gap-2"
              >
                <span className="material-symbols-outlined text-base">save</span>
                {savingEmail ? 'Guardando...' : 'Actualizar Correo'}
              </button>
            </form>
            <p className="mt-4 text-[11px] text-calm-accent font-light max-w-md">
              Se enviará un correo de confirmación a tu nueva dirección. Debes verificar el correo para completar el cambio.
            </p>
          </div>

          {/* Notification Preferences Section */}
          <div className="bg-gradient-to-br from-white to-slate-50 border border-slate-200/60 rounded-huge p-8 hover:shadow-sm transition-all cursor-pointer group">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-slate-50 flex items-center justify-center text-calm-accent">
                <span className="material-symbols-outlined text-xl">notifications</span>
              </div>
              <div>
                <h3 className="text-sm font-light text-calm-text">Preferencias de Notificaciones</h3>
                <p className="text-[11px] text-calm-accent font-light">Configura qué notificaciones deseas recibir por correo electrónico</p>
              </div>
            </div>
            {loading ? (
              <div className="flex flex-col gap-4 py-4 max-w-md">
                <Skeleton className="h-10 w-full rounded-huge" />
                <Skeleton className="h-10 w-full rounded-huge" />
                <Skeleton className="h-10 w-3/4 rounded-huge" />
              </div>
            ) : (
              <div className="space-y-4 max-w-lg">
                {/* Documentos */}
                <div className="border-b border-calm-border pb-4">
                  <p className="text-[10px] uppercase tracking-widest text-calm-accent mb-3">Documentos</p>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-light text-calm-text">Documentos Subidos</p>
                        <p className="text-[11px] text-calm-accent font-light">
                          Recibir notificación cuando se suban nuevos documentos
                        </p>
                      </div>
                      <Switch
                        checked={notificationSettings.documentosSubidos}
                        onCheckedChange={(checked) => handleNotificationChange('documentosSubidos', checked)}
                        disabled={savingNotifications}
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-light text-calm-text">Documentos Contabilizados</p>
                        <p className="text-[11px] text-calm-accent font-light">
                          Recibir notificación cuando un documento quede contabilizado
                        </p>
                      </div>
                      <Switch
                        checked={notificationSettings.documentosContabilizados}
                        onCheckedChange={(checked) => handleNotificationChange('documentosContabilizados', checked)}
                        disabled={savingNotifications}
                      />
                    </div>
                  </div>
                </div>

                {/* Incidencias */}
                <div className="border-b border-calm-border pb-4">
                  <p className="text-[10px] uppercase tracking-widest text-calm-accent mb-3">Incidencias</p>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-light text-calm-text">Incidencias Nuevas</p>
                        <p className="text-[11px] text-calm-accent font-light">
                          Recibir notificación cuando se abra una nueva incidencia
                        </p>
                      </div>
                      <Switch
                        checked={notificationSettings.incidenciasNuevas}
                        onCheckedChange={(checked) => handleNotificationChange('incidenciasNuevas', checked)}
                        disabled={savingNotifications}
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-light text-calm-text">Incidencias Resueltas</p>
                        <p className="text-[11px] text-calm-accent font-light">
                          Recibir notificación cuando se resuelva una incidencia
                        </p>
                      </div>
                      <Switch
                        checked={notificationSettings.incidenciasResueltas}
                        onCheckedChange={(checked) => handleNotificationChange('incidenciasResueltas', checked)}
                        disabled={savingNotifications}
                      />
                    </div>
                  </div>
                </div>

                {/* Informes e IA */}
                <div className="border-b border-calm-border pb-4">
                  <p className="text-[10px] uppercase tracking-widest text-calm-accent mb-3">Informes e IA</p>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-light text-calm-text">Informes Disponibles</p>
                        <p className="text-[11px] text-calm-accent font-light">
                          Recibir notificación cuando haya nuevos informes listos
                        </p>
                      </div>
                      <Switch
                        checked={notificationSettings.informesDisponibles}
                        onCheckedChange={(checked) => handleNotificationChange('informesDisponibles', checked)}
                        disabled={savingNotifications}
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-light text-calm-text">Recordatorios de IA</p>
                        <p className="text-[11px] text-calm-accent font-light">
                          Recibir recordatorios automáticos generados por la IA
                        </p>
                      </div>
                      <Switch
                        checked={notificationSettings.recordatoriosIA}
                        onCheckedChange={(checked) => handleNotificationChange('recordatoriosIA', checked)}
                        disabled={savingNotifications}
                      />
                    </div>
                  </div>
                </div>

                {/* Fiscal y Plataforma */}
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-calm-accent mb-3">Fiscal y Plataforma</p>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-light text-calm-text">Vencimientos Fiscales</p>
                        <p className="text-[11px] text-calm-accent font-light">
                          Recibir alertas de vencimientos fiscales próximos
                        </p>
                      </div>
                      <Switch
                        checked={notificationSettings.vencimientosFiscales}
                        onCheckedChange={(checked) => handleNotificationChange('vencimientosFiscales', checked)}
                        disabled={savingNotifications}
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-light text-calm-text">Actualizaciones de la Plataforma</p>
                        <p className="text-[11px] text-calm-accent font-light">
                          Recibir noticias y mejoras de la plataforma
                        </p>
                      </div>
                      <Switch
                        checked={notificationSettings.actualizacionesPlataforma}
                        onCheckedChange={(checked) => handleNotificationChange('actualizacionesPlataforma', checked)}
                        disabled={savingNotifications}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </section>
      </div>
    </Layout>
  );
}
